"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports["default"] = void 0;

var _util = require("../util.js");

var _size = require("../size.js");

/**
 * @module ol/style/Image
 */

/**
 * @typedef {Object} Options
 * @property {number} opacity Opacity.
 * @property {boolean} rotateWithView If the image should get rotated with the view.
 * @property {number} rotation Rotation.
 * @property {number|import("../size.js").Size} scale Scale.
 * @property {Array<number>} displacement Displacement.
 */

/**
 * @classdesc
 * A base class used for creating subclasses and not instantiated in
 * apps. Base class for {@link module:ol/style/Icon~Icon}, {@link module:ol/style/Circle~CircleStyle} and
 * {@link module:ol/style/RegularShape~RegularShape}.
 * @abstract
 * @api
 */
var ImageStyle =
/** @class */
function () {
  /**
   * @param {Options} options Options.
   */
  function ImageStyle(options) {
    /**
     * @private
     * @type {number}
     */
    this.opacity_ = options.opacity;
    /**
     * @private
     * @type {boolean}
     */

    this.rotateWithView_ = options.rotateWithView;
    /**
     * @private
     * @type {number}
     */

    this.rotation_ = options.rotation;
    /**
     * @private
     * @type {number|import("../size.js").Size}
     */

    this.scale_ = options.scale;
    /**
     * @private
     * @type {import("../size.js").Size}
     */

    this.scaleArray_ = (0, _size.toSize)(options.scale);
    /**
     * @private
     * @type {Array<number>}
     */

    this.displacement_ = options.displacement;
  }
  /**
   * Clones the style.
   * @return {ImageStyle} The cloned style.
   * @api
   */


  ImageStyle.prototype.clone = function () {
    var scale = this.getScale();
    return new ImageStyle({
      opacity: this.getOpacity(),
      scale: Array.isArray(scale) ? scale.slice() : scale,
      rotation: this.getRotation(),
      rotateWithView: this.getRotateWithView(),
      displacement: this.getDisplacement().slice()
    });
  };
  /**
   * Get the symbolizer opacity.
   * @return {number} Opacity.
   * @api
   */


  ImageStyle.prototype.getOpacity = function () {
    return this.opacity_;
  };
  /**
   * Determine whether the symbolizer rotates with the map.
   * @return {boolean} Rotate with map.
   * @api
   */


  ImageStyle.prototype.getRotateWithView = function () {
    return this.rotateWithView_;
  };
  /**
   * Get the symoblizer rotation.
   * @return {number} Rotation.
   * @api
   */


  ImageStyle.prototype.getRotation = function () {
    return this.rotation_;
  };
  /**
   * Get the symbolizer scale.
   * @return {number|import("../size.js").Size} Scale.
   * @api
   */


  ImageStyle.prototype.getScale = function () {
    return this.scale_;
  };
  /**
   * Get the symbolizer scale array.
   * @return {import("../size.js").Size} Scale array.
   */


  ImageStyle.prototype.getScaleArray = function () {
    return this.scaleArray_;
  };
  /**
   * Get the displacement of the shape
   * @return {Array<number>} Shape's center displacement
   * @api
   */


  ImageStyle.prototype.getDisplacement = function () {
    return this.displacement_;
  };
  /**
   * Get the anchor point in pixels. The anchor determines the center point for the
   * symbolizer.
   * @abstract
   * @return {Array<number>} Anchor.
   */


  ImageStyle.prototype.getAnchor = function () {
    return (0, _util["abstract"])();
  };
  /**
   * Get the image element for the symbolizer.
   * @abstract
   * @param {number} pixelRatio Pixel ratio.
   * @return {HTMLCanvasElement|HTMLVideoElement|HTMLImageElement} Image element.
   */


  ImageStyle.prototype.getImage = function (pixelRatio) {
    return (0, _util["abstract"])();
  };
  /**
   * @abstract
   * @return {HTMLCanvasElement|HTMLVideoElement|HTMLImageElement} Image element.
   */


  ImageStyle.prototype.getHitDetectionImage = function () {
    return (0, _util["abstract"])();
  };
  /**
   * Get the image pixel ratio.
   * @param {number} pixelRatio Pixel ratio.
   * @return {number} Pixel ratio.
   */


  ImageStyle.prototype.getPixelRatio = function (pixelRatio) {
    return 1;
  };
  /**
   * @abstract
   * @return {import("../ImageState.js").default} Image state.
   */


  ImageStyle.prototype.getImageState = function () {
    return (0, _util["abstract"])();
  };
  /**
   * @abstract
   * @return {import("../size.js").Size} Image size.
   */


  ImageStyle.prototype.getImageSize = function () {
    return (0, _util["abstract"])();
  };
  /**
   * Get the origin of the symbolizer.
   * @abstract
   * @return {Array<number>} Origin.
   */


  ImageStyle.prototype.getOrigin = function () {
    return (0, _util["abstract"])();
  };
  /**
   * Get the size of the symbolizer (in pixels).
   * @abstract
   * @return {import("../size.js").Size} Size.
   */


  ImageStyle.prototype.getSize = function () {
    return (0, _util["abstract"])();
  };
  /**
   * Set the displacement.
   *
   * @param {Array<number>} displacement Displacement.
   * @api
   */


  ImageStyle.prototype.setDisplacement = function (displacement) {
    this.displacement_ = displacement;
  };
  /**
   * Set the opacity.
   *
   * @param {number} opacity Opacity.
   * @api
   */


  ImageStyle.prototype.setOpacity = function (opacity) {
    this.opacity_ = opacity;
  };
  /**
   * Set whether to rotate the style with the view.
   *
   * @param {boolean} rotateWithView Rotate with map.
   * @api
   */


  ImageStyle.prototype.setRotateWithView = function (rotateWithView) {
    this.rotateWithView_ = rotateWithView;
  };
  /**
   * Set the rotation.
   *
   * @param {number} rotation Rotation.
   * @api
   */


  ImageStyle.prototype.setRotation = function (rotation) {
    this.rotation_ = rotation;
  };
  /**
   * Set the scale.
   *
   * @param {number|import("../size.js").Size} scale Scale.
   * @api
   */


  ImageStyle.prototype.setScale = function (scale) {
    this.scale_ = scale;
    this.scaleArray_ = (0, _size.toSize)(scale);
  };
  /**
   * @abstract
   * @param {function(import("../events/Event.js").default): void} listener Listener function.
   */


  ImageStyle.prototype.listenImageChange = function (listener) {
    (0, _util["abstract"])();
  };
  /**
   * Load not yet loaded URI.
   * @abstract
   */


  ImageStyle.prototype.load = function () {
    (0, _util["abstract"])();
  };
  /**
   * @abstract
   * @param {function(import("../events/Event.js").default): void} listener Listener function.
   */


  ImageStyle.prototype.unlistenImageChange = function (listener) {
    (0, _util["abstract"])();
  };

  return ImageStyle;
}();

var _default = ImageStyle;
exports["default"] = _default;
//# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJtYXBwaW5ncyI6Ijs7Ozs7OztBQUdBOztBQUNBOztBQUpBOzs7O0FBTUE7Ozs7Ozs7OztBQVNBOzs7Ozs7OztBQVFBO0FBQUE7QUFBQTtFQUNFOzs7RUFHQSxvQkFBWUEsT0FBWixFQUFtQjtJQUNqQjs7OztJQUlBLEtBQUtDLFFBQUwsR0FBZ0JELE9BQU8sQ0FBQ0UsT0FBeEI7SUFFQTs7Ozs7SUFJQSxLQUFLQyxlQUFMLEdBQXVCSCxPQUFPLENBQUNJLGNBQS9CO0lBRUE7Ozs7O0lBSUEsS0FBS0MsU0FBTCxHQUFpQkwsT0FBTyxDQUFDTSxRQUF6QjtJQUVBOzs7OztJQUlBLEtBQUtDLE1BQUwsR0FBY1AsT0FBTyxDQUFDUSxLQUF0QjtJQUVBOzs7OztJQUlBLEtBQUtDLFdBQUwsR0FBbUIsa0JBQU9ULE9BQU8sQ0FBQ1EsS0FBZixDQUFuQjtJQUVBOzs7OztJQUlBLEtBQUtFLGFBQUwsR0FBcUJWLE9BQU8sQ0FBQ1csWUFBN0I7RUFDRDtFQUVEOzs7Ozs7O0VBS0FDO0lBQ0UsSUFBTUosS0FBSyxHQUFHLEtBQUtLLFFBQUwsRUFBZDtJQUNBLE9BQU8sSUFBSUQsVUFBSixDQUFlO01BQ3BCVixPQUFPLEVBQUUsS0FBS1ksVUFBTCxFQURXO01BRXBCTixLQUFLLEVBQUVPLEtBQUssQ0FBQ0MsT0FBTixDQUFjUixLQUFkLElBQXVCQSxLQUFLLENBQUNTLEtBQU4sRUFBdkIsR0FBdUNULEtBRjFCO01BR3BCRixRQUFRLEVBQUUsS0FBS1ksV0FBTCxFQUhVO01BSXBCZCxjQUFjLEVBQUUsS0FBS2UsaUJBQUwsRUFKSTtNQUtwQlIsWUFBWSxFQUFFLEtBQUtTLGVBQUwsR0FBdUJILEtBQXZCO0lBTE0sQ0FBZixDQUFQO0VBT0QsQ0FURDtFQVdBOzs7Ozs7O0VBS0FMO0lBQ0UsT0FBTyxLQUFLWCxRQUFaO0VBQ0QsQ0FGRDtFQUlBOzs7Ozs7O0VBS0FXO0lBQ0UsT0FBTyxLQUFLVCxlQUFaO0VBQ0QsQ0FGRDtFQUlBOzs7Ozs7O0VBS0FTO0lBQ0UsT0FBTyxLQUFLUCxTQUFaO0VBQ0QsQ0FGRDtFQUlBOzs7Ozs7O0VBS0FPO0lBQ0UsT0FBTyxLQUFLTCxNQUFaO0VBQ0QsQ0FGRDtFQUlBOzs7Ozs7RUFJQUs7SUFDRSxPQUFPLEtBQUtILFdBQVo7RUFDRCxDQUZEO0VBSUE7Ozs7Ozs7RUFLQUc7SUFDRSxPQUFPLEtBQUtGLGFBQVo7RUFDRCxDQUZEO0VBSUE7Ozs7Ozs7O0VBTUFFO0lBQ0UsT0FBTyx3QkFBUDtFQUNELENBRkQ7RUFJQTs7Ozs7Ozs7RUFNQUEsMENBQVNTLFVBQVQsRUFBbUI7SUFDakIsT0FBTyx3QkFBUDtFQUNELENBRkQ7RUFJQTs7Ozs7O0VBSUFUO0lBQ0UsT0FBTyx3QkFBUDtFQUNELENBRkQ7RUFJQTs7Ozs7OztFQUtBQSwrQ0FBY1MsVUFBZCxFQUF3QjtJQUN0QixPQUFPLENBQVA7RUFDRCxDQUZEO0VBSUE7Ozs7OztFQUlBVDtJQUNFLE9BQU8sd0JBQVA7RUFDRCxDQUZEO0VBSUE7Ozs7OztFQUlBQTtJQUNFLE9BQU8sd0JBQVA7RUFDRCxDQUZEO0VBSUE7Ozs7Ozs7RUFLQUE7SUFDRSxPQUFPLHdCQUFQO0VBQ0QsQ0FGRDtFQUlBOzs7Ozs7O0VBS0FBO0lBQ0UsT0FBTyx3QkFBUDtFQUNELENBRkQ7RUFJQTs7Ozs7Ozs7RUFNQUEsaURBQWdCRCxZQUFoQixFQUE0QjtJQUMxQixLQUFLRCxhQUFMLEdBQXFCQyxZQUFyQjtFQUNELENBRkQ7RUFJQTs7Ozs7Ozs7RUFNQUMsNENBQVdWLE9BQVgsRUFBa0I7SUFDaEIsS0FBS0QsUUFBTCxHQUFnQkMsT0FBaEI7RUFDRCxDQUZEO0VBSUE7Ozs7Ozs7O0VBTUFVLG1EQUFrQlIsY0FBbEIsRUFBZ0M7SUFDOUIsS0FBS0QsZUFBTCxHQUF1QkMsY0FBdkI7RUFDRCxDQUZEO0VBSUE7Ozs7Ozs7O0VBTUFRLDZDQUFZTixRQUFaLEVBQW9CO0lBQ2xCLEtBQUtELFNBQUwsR0FBaUJDLFFBQWpCO0VBQ0QsQ0FGRDtFQUdBOzs7Ozs7OztFQU1BTSwwQ0FBU0osS0FBVCxFQUFjO0lBQ1osS0FBS0QsTUFBTCxHQUFjQyxLQUFkO0lBQ0EsS0FBS0MsV0FBTCxHQUFtQixrQkFBT0QsS0FBUCxDQUFuQjtFQUNELENBSEQ7RUFLQTs7Ozs7O0VBSUFJLG1EQUFrQlUsUUFBbEIsRUFBMEI7SUFDeEI7RUFDRCxDQUZEO0VBSUE7Ozs7OztFQUlBVjtJQUNFO0VBQ0QsQ0FGRDtFQUlBOzs7Ozs7RUFJQUEscURBQW9CVSxRQUFwQixFQUE0QjtJQUMxQjtFQUNELENBRkQ7O0VBR0Y7QUFBQyxDQS9QRDs7ZUFpUWVWLFUiLCJuYW1lcyI6WyJvcHRpb25zIiwib3BhY2l0eV8iLCJvcGFjaXR5Iiwicm90YXRlV2l0aFZpZXdfIiwicm90YXRlV2l0aFZpZXciLCJyb3RhdGlvbl8iLCJyb3RhdGlvbiIsInNjYWxlXyIsInNjYWxlIiwic2NhbGVBcnJheV8iLCJkaXNwbGFjZW1lbnRfIiwiZGlzcGxhY2VtZW50IiwiSW1hZ2VTdHlsZSIsImdldFNjYWxlIiwiZ2V0T3BhY2l0eSIsIkFycmF5IiwiaXNBcnJheSIsInNsaWNlIiwiZ2V0Um90YXRpb24iLCJnZXRSb3RhdGVXaXRoVmlldyIsImdldERpc3BsYWNlbWVudCIsInBpeGVsUmF0aW8iLCJsaXN0ZW5lciJdLCJzb3VyY2VSb290IjoiIiwic291cmNlcyI6WyIuLi9zcmMvc3R5bGUvSW1hZ2UuanMiXSwic291cmNlc0NvbnRlbnQiOltudWxsXX0=